// src/services/adminAuth.js

// Local Storage Keys
const KEY_TOKEN = "admin.token";
const KEY_USER  = "admin.user";
const KEY_EXP   = "admin.exp";

// Optional: Global event so React components / other tabs can react to login/logout
export const AUTH_EVENT = "saam-auth-change";

// ---- Core session helpers ----
function emitAuthChange(payload = {}) {
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: payload }));
  } catch {}
}

function readExp() {
  const n = Number(localStorage.getItem(KEY_EXP) || 0);
  return Number.isFinite(n) ? n : 0;
}

export function remainingMs() {
  const exp = readExp();
  return Math.max(0, exp - Date.now());
}

export function isExpired() {
  return remainingMs() <= 0;
}

export function getToken() {
  return localStorage.getItem(KEY_TOKEN) || null;
}

export function getUser() {
  const str = localStorage.getItem(KEY_USER);
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

// ---- Public API ----

// Save session (4 hours default)
export function setSession({ token, user, ttlMinutes = 240 }) {
  const exp = Date.now() + ttlMinutes * 60 * 1000;
  localStorage.setItem(KEY_TOKEN, token || "local-session");
  localStorage.setItem(KEY_USER, JSON.stringify(user || { role: "admin" }));
  localStorage.setItem(KEY_EXP, String(exp));
  emitAuthChange({ type: "login", user, exp });
}

// Extend session if user is active (e.g., call on route change, click, etc.)
export function touchSession({ extendMinutes = 30, thresholdMinutes = 10 } = {}) {
  const exp = readExp();
  if (!exp) return;
  const remaining = exp - Date.now();
  const threshold = thresholdMinutes * 60 * 1000;
  if (remaining > 0 && remaining <= threshold) {
    // Roll the expiry forward
    const newExp = Date.now() + extendMinutes * 60 * 1000;
    localStorage.setItem(KEY_EXP, String(newExp));
    emitAuthChange({ type: "touch", exp: newExp });
  }
}

export function clearSession() {
  localStorage.removeItem(KEY_TOKEN);
  localStorage.removeItem(KEY_USER);
  localStorage.removeItem(KEY_EXP);
  emitAuthChange({ type: "logout" });
}

export function getSession() {
  const token = getToken();
  const user  = getUser();
  const exp   = readExp();
  if (!token || !user || !exp || Date.now() > exp) {
    clearSession();
    return null;
  }
  return { token, user, exp };
}

export function isAuthed() {
  return !!getSession();
}

export function hasAdminRole() {
  const s = getSession();
  const role = s?.user?.role;
  return role && String(role).toLowerCase() === "admin";
}

// Dummy local login (replace with Firebase Auth or backend)
export async function adminLogin({ email, password }) {
  const ALLOWED = [
    { email: "admin@shineagri.org", pass: "Admin@123", role: "admin", name: "Admin User" },
  ];
  const ok = ALLOWED.find(u => u.email === email && u.pass === password);
  if (!ok) return false;

  setSession({
    token: "local-token",
    user: { email, role: ok.role, name: ok.name },
  });
  return true;
}

/**
 * Logout (FULL)
 * - Tries to sign out from Firebase if available (so ProtectedRoute won't auto-allow)
 * - Clears local session keys
 * - Optionally removes leftover "firebase:*" localStorage keys (hard kill)
 */
export async function adminLogout() {
  // 1) Firebase sign-out (safe optional)
  try {
    // adjust path if your firebaseConfig lives elsewhere
    const { auth } = await import("../firebaseConfig");
    const { signOut } = await import("firebase/auth");
    if (auth && auth.currentUser) {
      await signOut(auth);
    }
  } catch {
    // ignore if firebase not configured here
  }

  // 2) Local session clear
  clearSession();

  // 3) Optional: purge lingering firebase localStorage entries
  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (k.startsWith("firebase:")) {
        localStorage.removeItem(k);
      }
    }
  } catch {}
}

// Subscribe to auth changes (also listens to multi-tab storage changes)
export function onAuthChange(callback) {
  const handler = (e) => {
    if (e.type === AUTH_EVENT) {
      callback(e.detail);
      return;
    }
    // Cross-tab sync: if any of our keys change, notify
    if ([KEY_TOKEN, KEY_USER, KEY_EXP].includes(e.key)) {
      callback({ type: "storage" });
    }
  };

  window.addEventListener(AUTH_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(AUTH_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}
